import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    obterConfiguracoesSistema,
    atualizarLogging,
    atualizarBrasaoOficial,
} from '@/services/configuracoes-sistema.service';
import { atualizarEstadoLgpd } from '@/services/lgpd.service';
import {
    PREFERENCIA_AUDITORIA,
    PREFERENCIA_LIBRAS,
    PREFERENCIA_TUTORIAL,
    setPreferencia,
    subscribePreferencias,
    syncPreferenciasFromApi,
} from '@/services/preferencias.service';
import { notificarErro, notificarSucesso } from '@/services/toast.service';
import { getLoginAD } from '@/core/configs/auth/auth.utils';
import { useAuth } from '@/core/hooks/useAuth';

export interface ConfiguracoesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ConfiguracoesModal: React.FC<ConfiguracoesModalProps> = ({ isOpen, onClose }) => {
    const { usuarioSessao } = useAuth();
    const [loggingAtivo, setLoggingAtivo] = useState(false);
    const [lgpdAtivo, setLgpdAtivo] = useState(false);
    const [tutorialAtivo, setTutorialAtivo] = useState(true);
    const [auditoriaAtiva, setAuditoriaAtiva] = useState(true);
    const [librasAtivo, setLibrasAtivo] = useState(true);
    const [brasaoOficialAtivo, setBrasaoOficialAtivo] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [atualizandoLogging, setAtualizandoLogging] = useState(false);
    const [atualizandoPreferencias, setAtualizandoPreferencias] = useState(false);
    const [atualizandoLgpd, setAtualizandoLgpd] = useState(false);
    const [atualizandoBrasao, setAtualizandoBrasao] = useState(false);

    const loginAD = getLoginAD(usuarioSessao);
    const salvandoPreferencias = atualizandoPreferencias;

    useEffect(() => {
        const unsubscribe = subscribePreferencias((preferencias) => {
            setTutorialAtivo(preferencias[PREFERENCIA_TUTORIAL] === false ? false : true);
            setAuditoriaAtiva(preferencias[PREFERENCIA_AUDITORIA] === false ? false : true);
            setLibrasAtivo(preferencias[PREFERENCIA_LIBRAS] === false ? false : true);
        });
        return unsubscribe;
    }, []);

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            setCarregando(true);
            try {
                await syncPreferenciasFromApi(loginAD);
                if (cancelled) return;

                const config = await obterConfiguracoesSistema();
                if (cancelled) return;

                setLoggingAtivo(Boolean(config?.logging));
                setLgpdAtivo(Boolean(config?.lgpd));
                setBrasaoOficialAtivo(Boolean(config?.brasaoOficial));
            } catch {
                if (!cancelled) {
                    notificarErro(['Não foi possível carregar as configurações.']);
                }
            } finally {
                if (!cancelled) {
                    setCarregando(false);
                }
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [isOpen, loginAD]);

    const handleLoggingChange = useCallback(
        async (novoStatus: boolean) => {
            if (atualizandoLogging || atualizandoLgpd) return;
            setAtualizandoLogging(true);
            const resultado = await atualizarLogging(novoStatus);
            setAtualizandoLogging(false);
            if (!resultado.success) {
                setLoggingAtivo(!novoStatus);
                notificarErro(resultado.errors);
                return;
            }
            notificarSucesso([`Logging ${novoStatus ? 'ativado' : 'desativado'} com sucesso.`]);
        },
        [atualizandoLogging, atualizandoLgpd]
    );

    const handleTutorialChange = useCallback(
        async (novoStatus: boolean) => {
            if (!loginAD || atualizandoPreferencias || atualizandoLgpd) return;
            setAtualizandoPreferencias(true);
            try {
                await setPreferencia(loginAD, PREFERENCIA_TUTORIAL, novoStatus);
            } catch {
                notificarErro(['Não foi possível salvar a preferência de Tutorial.']);
                setTutorialAtivo(!novoStatus);
            } finally {
                setAtualizandoPreferencias(false);
            }
        },
        [loginAD, atualizandoPreferencias, atualizandoLgpd]
    );

    const handleAuditoriaChange = useCallback(
        async (novoStatus: boolean) => {
            if (!loginAD || atualizandoPreferencias || atualizandoLgpd) return;
            setAtualizandoPreferencias(true);
            try {
                await setPreferencia(loginAD, PREFERENCIA_AUDITORIA, novoStatus);
            } catch {
                notificarErro(['Não foi possível salvar a preferência de Auditoria.']);
                setAuditoriaAtiva(!novoStatus);
            } finally {
                setAtualizandoPreferencias(false);
            }
        },
        [loginAD, atualizandoPreferencias, atualizandoLgpd]
    );

    const handleLibrasChange = useCallback(
        async (novoStatus: boolean) => {
            if (!loginAD || atualizandoPreferencias || atualizandoLgpd) return;
            setAtualizandoPreferencias(true);
            try {
                await setPreferencia(loginAD, PREFERENCIA_LIBRAS, novoStatus);
                notificarSucesso([
                    `Assistência em Libras ${novoStatus ? 'ativada' : 'desativada'} com sucesso.`,
                ]);
            } catch {
                setLibrasAtivo(!novoStatus);
                notificarErro(['Não foi possível salvar a preferência de Libras.']);
            } finally {
                setAtualizandoPreferencias(false);
            }
        },
        [loginAD, atualizandoPreferencias, atualizandoLgpd]
    );

    const handleBrasaoOficialChange = useCallback(
        async (novoStatus: boolean) => {
            if (atualizandoBrasao || atualizandoLogging || atualizandoLgpd) return;
            setAtualizandoBrasao(true);
            const resultado = await atualizarBrasaoOficial(novoStatus);
            setAtualizandoBrasao(false);
            if (!resultado.success) {
                setBrasaoOficialAtivo(!novoStatus);
                notificarErro(resultado.errors);
                return;
            }
            notificarSucesso([
                `Brasão oficial ${novoStatus ? 'ativado' : 'desativado'} com sucesso.`,
            ]);
        },
        [atualizandoBrasao, atualizandoLogging, atualizandoLgpd]
    );

    const handleLgpdChange = useCallback(
        async (novoStatus: boolean) => {
            if (atualizandoLgpd || atualizandoLogging || atualizandoPreferencias) return;
            setAtualizandoLgpd(true);
            try {
                await atualizarEstadoLgpd(novoStatus);
                notificarSucesso([`LGPD ${novoStatus ? 'ativado' : 'desativado'} com sucesso.`]);
            } catch {
                setLgpdAtivo(!novoStatus);
                notificarErro(['Não foi possível atualizar a configuração LGPD.']);
            } finally {
                setAtualizandoLgpd(false);
            }
        },
        [atualizandoLgpd, atualizandoLogging, atualizandoPreferencias]
    );

    if (!isOpen || typeof document === 'undefined') {
        return null;
    }

    const modalContent = (
        <>
            <div
                className="modal-backdrop fade show configuracoes-modal-backdrop"
                onClick={onClose}
                role="presentation"
            />
            <div
                className="modal fade show logging-config-window d-block"
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="configuracoes-modal-title"
                onClick={onClose}>
                <div
                    className="modal-dialog modal-dialog-centered"
                    onClick={(event) => event.stopPropagation()}>
                    <div className="modal-content">
                        <div className="modal-header py-3 px-4">
                            <h4 className="modal-title" id="configuracoes-modal-title">
                                Configurações
                            </h4>
                            <button
                                type="button"
                                className="confirm-close-btn"
                                aria-label="Fechar"
                                onClick={onClose}
                                disabled={carregando}>
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path
                                        d="M18 6L6 18M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body px-4 py-3">
                            {carregando ? (
                                <div className="configuracoes-loading">Carregando configurações...</div>
                            ) : (
                                <>
                                    <div className="logging-toggle-row">
                                        <span className="logging-label">Habilitar Registro de Logging</span>
                                        <div className="form-check form-switch logging-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={loggingAtivo}
                                                onChange={(event) => {
                                                    const novoStatus = event.target.checked;
                                                    setLoggingAtivo(novoStatus);
                                                    void handleLoggingChange(novoStatus);
                                                }}
                                                disabled={carregando || atualizandoLogging || atualizandoLgpd}
                                            />
                                        </div>
                                    </div>

                                    <div className="logging-toggle-row">
                                        <span className="logging-label">Habilitar Tutorial</span>
                                        <div className="form-check form-switch logging-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={tutorialAtivo}
                                                onChange={(event) => {
                                                    const novoStatus = event.target.checked;
                                                    setTutorialAtivo(novoStatus);
                                                    void handleTutorialChange(novoStatus);
                                                }}
                                                disabled={carregando || salvandoPreferencias}
                                            />
                                        </div>
                                    </div>

                                    <div className="logging-toggle-row">
                                        <span className="logging-label">Habilitar Auditoria</span>
                                        <div className="form-check form-switch logging-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={auditoriaAtiva}
                                                onChange={(event) => {
                                                    const novoStatus = event.target.checked;
                                                    setAuditoriaAtiva(novoStatus);
                                                    void handleAuditoriaChange(novoStatus);
                                                }}
                                                disabled={carregando || salvandoPreferencias}
                                            />
                                        </div>
                                    </div>

                                    <div className="logging-toggle-row">
                                        <span className="logging-label">Habilitar assistência em Libras</span>
                                        <div className="form-check form-switch logging-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={librasAtivo}
                                                onChange={(event) => {
                                                    const novoStatus = event.target.checked;
                                                    setLibrasAtivo(novoStatus);
                                                    void handleLibrasChange(novoStatus);
                                                }}
                                                disabled={carregando || salvandoPreferencias}
                                            />
                                        </div>
                                    </div>

                                    <div className="logging-toggle-row">
                                        <span className="logging-label">Ativar brasão oficial</span>
                                        <div className="form-check form-switch logging-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={brasaoOficialAtivo}
                                                onChange={(event) => {
                                                    const novoStatus = event.target.checked;
                                                    setBrasaoOficialAtivo(novoStatus);
                                                    void handleBrasaoOficialChange(novoStatus);
                                                }}
                                                disabled={carregando || atualizandoBrasao || atualizandoLogging || atualizandoLgpd}
                                            />
                                        </div>
                                    </div>

                                    <div className="logging-toggle-row">
                                        <span className="logging-label">Habilitar LGPD</span>
                                        <div className="form-check form-switch logging-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={lgpdAtivo}
                                                onChange={(event) => {
                                                    const novoStatus = event.target.checked;
                                                    setLgpdAtivo(novoStatus);
                                                    void handleLgpdChange(novoStatus);
                                                }}
                                                disabled={carregando || atualizandoLgpd || atualizandoLogging || salvandoPreferencias}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="modal-footer px-4 py-3">
                            <button
                                type="button"
                                id="btn-configuracoes-sair"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={
                                    atualizandoLogging ||
                                    atualizandoPreferencias ||
                                    atualizandoLgpd ||
                                    atualizandoBrasao
                                }>
                                Sair
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return createPortal(modalContent, document.body);
};

export default ConfiguracoesModal;
